import { IS_NATIVE_APP } from '../config/environment'

/** Semantic feedback kinds; callers describe the interaction, not the vibration. */
export type HapticKind = 'tap' | 'toggle' | 'select' | 'reveal' | 'threshold'

const STORAGE_KEY = 'tunetoss.hapticsEnabled'

let selectionStarted = false

/** Device-local preference: haptics belong to this phone, not the account. */
export function readHapticsPreference(): boolean {
	try {
		return localStorage.getItem(STORAGE_KEY) !== 'false'
	} catch {
		return true
	}
}

export function saveHapticsPreference(enabled: boolean): void {
	try {
		localStorage.setItem(STORAGE_KEY, String(enabled))
	} catch {
		// Keep the current session usable when device storage is unavailable.
	}
}

function hapticsAllowed(): boolean {
	return (
		IS_NATIVE_APP &&
		readHapticsPreference() &&
		!window.matchMedia('(prefers-reduced-motion: reduce)').matches
	)
}

/** Optional feedback must never interrupt the interaction that triggered it. */
export async function haptic(kind: HapticKind): Promise<void> {
	if (!hapticsAllowed()) return
	try {
		const { Haptics, ImpactStyle } = await import('@capacitor/haptics')
		if (kind === 'select') {
			// iOS ignores selection changes until a generator was started.
			if (!selectionStarted) {
				selectionStarted = true
				await Haptics.selectionStart()
			}
			await Haptics.selectionChanged()
			return
		}
		await Haptics.impact({
			style:
				kind === 'reveal' || kind === 'threshold'
					? ImpactStyle.Medium
					: ImpactStyle.Light,
		})
	} catch {
		// Devices without haptic support keep working without feedback.
	}
}

/** Gives every settings-style switch the same toggle feedback. */
export function setupSwitchHaptics(): void {
	if (!IS_NATIVE_APP) return
	document.addEventListener('change', (event) => {
		const target = event.target
		if (
			target instanceof HTMLInputElement &&
			target.type === 'checkbox' &&
			target.closest('.tt-switch')
		) {
			void haptic('toggle')
		}
	})
}
